import { IconExpandMore } from "@tine/designsystem-icons/sharp";
import { cn } from "@tine/designsystem-utils";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link } from "wouter";

import { sendInternalLinkClick } from "../../common/tracking";
import type { CategoryInfo } from "../../common/types/categoryTypes";
import type { MenuItem } from "../../common/types/cmsTypes";
import { titleCase } from "../../common/utils";
import ColumnDropdown from "../../components/common/ColumnDropdown";
import MainColumn from "../../components/common/MainColumn";

import categoryStore from "../../stores/categories/categoryStore";
import contentStore from "../../stores/cms/contentStore";
import segmentStore from "../../stores/cms/segmentStore";
import featuresStore from "../../stores/features/featuresStore";
import productStore from "../../stores/product/productStore";
import theme from "../../themes/theme";
import MegaMenu from "./MegaMenu";

type CategoryProps = {
   cat: CategoryInfo;
};

const itemCN = cn(["py-[--topnav-categories-padding-y]", "!text-headerFooterText"]);

const Category = observer(({ cat }: CategoryProps) => {
   const [show, setShow] = useState(false);
   const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);

   return (
      <>
         <div ref={setReferenceElement} className="font-medium">
            <button type="button" className={cn(itemCN, "ps-0")} onClick={() => setShow(!show)}>
               {titleCase(cat.name)}
               <IconExpandMore className="inline" />
            </button>
         </div>
         {!!referenceElement && (
            <ColumnDropdown open={show} referenceElement={referenceElement} onOpenChange={setShow}>
               <MegaMenu cat={cat} onHide={() => setShow(false)} />
            </ColumnDropdown>
         )}
      </>
   );
});

const categoriesClassName = cn(["w-full bg-headerFooterBackground"]);

const categoriesNavigationContainerClassName = cn(["flex shrink-0 grow flex-row items-center", "w-full py-[--topnav-padding-y]"]);

const categoriesItemsClassName = cn(["flex w-full flex-row items-center justify-start gap-4"]);

const Categories = () => {
   const { categories } = categoryStore;
   const { productDropdownEnabled } = theme;
   const isSideMenu = featuresStore.navigationTestVariant === "sidemenu";
   const isTopMenu = featuresStore.navigationTestVariant === "topmenu";
   const isMegaMenu = !isSideMenu && !isTopMenu;

   const categoryTree = productDropdownEnabled
      ? categories
      : (categories[0]?.children || categories).filter((cat) => productStore.resolveSkus(cat.skus, true).length);

   const isMenuItemVisible = (menuItem: MenuItem) =>
      (menuItem.contentType === "v2_article" && segmentStore.isArticleVisible(menuItem.id)) ||
      menuItem.contentType !== "v2_article";

   return (
      <div className={categoriesClassName}>
         <MainColumn>
            <nav className={categoriesNavigationContainerClassName}>
               <div className={categoriesItemsClassName}>
                  {isMegaMenu && productDropdownEnabled && categoryTree.map((cat) => <Category key={cat.id} cat={cat} />)}
                  {!(isMegaMenu && productDropdownEnabled) &&
                     categoryTree.map((cat) => (
                        <div key={cat.id} className="py-[--topnav-categories-padding-y] font-medium">
                           <Link
                              className={itemCN}
                              to={cat.fullUrl}
                              onClick={() => sendInternalLinkClick(cat.fullUrl, "Navbar", titleCase(cat.name))}
                           >
                              {titleCase(cat.name)}
                           </Link>
                        </div>
                     ))}
                  {contentStore.mainMenuItems.map(
                     (menuItem) =>
                        isMenuItemVisible(menuItem) && (
                           <div key={menuItem.id}>
                              <Link
                                 className={itemCN}
                                 to={menuItem.url}
                                 onClick={() => sendInternalLinkClick(menuItem.url, "Navbar", menuItem.title)}
                              >
                                 {menuItem.title}
                              </Link>
                           </div>
                        )
                  )}
               </div>
            </nav>
         </MainColumn>
      </div>
   );
};

export default observer(Categories);
